// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
} from 'react'
import {
  Checkbox,
  Flex,
  FormItem,
  Form,
  Select,
  Modal,
  Tag,
  Typography,
  UseShowHide,
  useForm,
  useWatch,
  Switch,
} from '@teamnine/pcj-ui-v2'
import { useNavigate } from 'react-router-dom'
import { CheckCircle } from '@/utils/material-icons'
import { useQueryClient, useQuery } from 'react-query'
import { useTheme } from 'styled-components'

import { Customer as ICustomer } from '@/services/customers'
import {
  createTenantFromTemplate,
  getNextgenPractices,
  getTemplates,
  NextgenPractice,
  Tenant as Practice,
  Template as ITemplate,
} from '@/services/practices'
import {
  normalizeName,
  PRACTICE_REGEX,
  extractPracticeName,
  getTagColor,
  getTagLabel,
} from '@/utils/helpers'
import { useErrorHelper } from '@/app/shared/hooks/useErrorHelper'
import TemplateJSON from '@/app/Templates/components/TemplateJSON'
import ObjectDataTable from '@/app/Templates/components/ObjectDataTable'
import LinkButton from '@/app/shared/components/LinkButton'
import {
  formatPracticeIdentifier,
  getNextgenPracticeOptionValue,
  getPracticeRejectionReason,
  getPracticeTenantName,
  hasRequiredPracticeIdentifiers,
  MISSING_GCN_MESSAGE,
  MISSING_PRACTICE_IDENTIFIERS_MESSAGE,
  UNSOURCED_PRACTICE_MESSAGE,
} from '@/app/CustomerManagement/utils/nextgen-practices'
import * as Styled from './Styles'

type CreatePracticeFromTemplateModalProps = {
  modal: UseShowHide
  customer?: ICustomer
  practices?: Practice[]
}

type FormValues = {
  practice: string
  practiceTemplate: string
  includeObjectData?: boolean
}

type PracticeOption = {
  label: string | ReactNode
  value: string
  labelValue: string
  searchText?: string
  disabled?: boolean
}

const FORM_NAME = 'new-practice'
const DEFAULT_TEMPLATE_ID = 'fa6e5a8d-9087-4cd3-80e4-ddb8bc7c2524'

export default function CreatePracticeFromTemplateModal({
  modal,
  customer,
  practices,
}: CreatePracticeFromTemplateModalProps) {
  const { colors } = useTheme()
  const { basicErrorHandler, displayGlobalMessage } = useErrorHelper()
  const [form] = useForm<FormValues>()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const [templates, setTemplates] = useState<ITemplate[]>([])
  const [includeDrafts, setIncludeDrafts] = useState(false)
  const [inspectJSON, setInspectJSON] = useState(false)
  const [inspectDataObjects, setInspectDataObjects] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const selectedTemplateId = useWatch('practiceTemplate', form)
  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId)
  const hasObjectData = !!selectedTemplate?.includesObjectData
  const userSelectedTemplateRef = useRef(false)

  const { isLoading: templatesLoading } = useQuery(
    ['getTemplates'],
    async () => {
      const data = await getTemplates()
      setTemplates(data || [])
      return data
    }
  )

  const [searchValue, setSearchValue] = useState('')

  const hasGcn = Boolean(
    customer?.accountGcn && String(customer.accountGcn).trim()
  )
  const normalizedSearch = normalizeName(searchValue).trim()

  const {
    data: nextgenData,
    isLoading: nextgenLoading,
    isFetching: nextgenFetching,
  } = useQuery(
    ['getNextgenPractices', customer?.accountGcn],
    async () => {
      if (!hasGcn) {
        return { practices: [] as NextgenPractice[] }
      }

      const practices = await getNextgenPractices({
        gcn: customer?.accountGcn ?? '',
      })
      return { practices: practices ?? [] }
    },
    {
      enabled: hasGcn,
      keepPreviousData: true,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 5,
      onError: (err) => {
        console.error('GetNextgenPractices failed', err)
      },
    }
  )

  const nextgenResults = useMemo(
    () => nextgenData?.practices ?? [],
    [nextgenData]
  )

  const nextgenLookup = useMemo(() => {
    const byValue = new Map<string, NextgenPractice>()

    nextgenResults.forEach((practice) => {
      byValue.set(getNextgenPracticeOptionValue(practice), practice)
    })

    return { byValue }
  }, [nextgenResults])

  const existingPractices = useMemo(() => {
    const set = new Set<string>()

    const addFromPractice = (practice: Practice) => {
      const candidateFromName = normalizeName(
        extractPracticeName(practice.name, customer?.name)
      )
      if (candidateFromName) set.add(candidateFromName)

      if (practice.subdomain) {
        set.add(normalizeName(practice.subdomain))
      }

      if (practice.auth0OrgId) {
        set.add(normalizeName(practice.auth0OrgId))
      }
    }

    if (practices) {
      practices.forEach(addFromPractice)
    }

    return set
  }, [practices, customer?.name])

  const practiceLookup = useMemo(() => {
    const map = new Map<string, Practice>()

    if (practices) {
      practices.forEach((practice) => {
        const fromName = normalizeName(
          extractPracticeName(practice.name, customer?.name)
        )
        if (fromName) map.set(fromName, practice)

        if (practice.subdomain) {
          map.set(normalizeName(practice.subdomain), practice)
        }

        if (practice.auth0OrgId) {
          map.set(normalizeName(practice.auth0OrgId), practice)
        }
      })
    }

    return map
  }, [practices, customer?.name])

  const practiceOptions = useMemo(() => {
    return Array.from(existingPractices || [])
      .filter(Boolean)
      .map((p) => ({
        label: (
          <Flex justify="space-between" align="start" gap={12} fullWidth>
            <Styled.Flex direction="column" gap={4}>
              <Typography variant="body-m-semi-bold" color={colors.nggray700}>
                {p}
              </Typography>
            </Styled.Flex>
            <Styled.CustomTag color="secondary">Existing</Styled.CustomTag>
          </Flex>
        ),
        value: p,
        labelValue: p,
        searchText: p,
        disabled: true,
      }))
  }, [existingPractices, colors.nggray700])

  const currentOptions = useMemo(() => {
    const q = normalizedSearch
    const map = new Map<string, PracticeOption>()

    practiceOptions.forEach((option) => map.set(option.value, option))

    nextgenResults.forEach((practice) => {
      const value = getNextgenPracticeOptionValue(practice)
      const normalizedPracticeName =
        normalizeName(practice.practiceName) || practice.practiceName
      const isExisting = existingPractices.has(normalizedPracticeName)

      map.set(value, {
        label: (
          <Flex justify="space-between" align="start" gap={12} fullWidth>
            <Styled.Flex direction="column" gap={8}>
              <Typography
                variant="body-m-semi-bold"
                color={isExisting ? colors.nggray700 : undefined}
              >
                {practice.practiceName}
              </Typography>
              <Styled.Wrapper>
                <Typography
                  variant="body-s-regular"
                  color={isExisting ? colors.nggray700 : undefined}
                >
                  {`Enterprise ID: ${formatPracticeIdentifier(practice.enterpriseId)} • Enterprise Practice ID: ${formatPracticeIdentifier(practice.enterprisePracticeId)} • Site ID: ${formatPracticeIdentifier(practice.siteId)}`}
                </Typography>
              </Styled.Wrapper>
            </Styled.Flex>
            {isExisting && (
              <Styled.NextGenTag color="secondary">Existing</Styled.NextGenTag>
            )}
          </Flex>
        ),
        value,
        labelValue: practice.practiceName,
        searchText: [
          practice.practiceName,
          formatPracticeIdentifier(practice.enterpriseId),
          formatPracticeIdentifier(practice.enterprisePracticeId),
          formatPracticeIdentifier(practice.siteId),
        ].join(' '),
        disabled: isExisting,
      })
    })

    return Array.from(map.values())
      .filter((option) => {
        if (!q) return true

        return (
          normalizeName(option.searchText ?? option.labelValue).includes(q) ||
          normalizeName(String(option.value)).includes(q)
        )
      })
      .sort((a, b) =>
        a.labelValue.localeCompare(b.labelValue, undefined, {
          sensitivity: 'base',
        })
      )
  }, [
    practiceOptions,
    nextgenResults,
    existingPractices,
    normalizedSearch,
    colors.nggray700,
  ])

  const isInitialPracticesLoading =
    hasGcn && !nextgenData && (nextgenLoading || nextgenFetching)
  const visibleOptions = isInitialPracticesLoading ? [] : currentOptions

  const filteredTemplates = useMemo(() => {
    const list = Array.isArray(templates) ? templates : []
    const base = includeDrafts ? list : list.filter((t) => t.published)

    return [...base].sort((a, b) => {
      if (a.id === DEFAULT_TEMPLATE_ID) return -1
      if (b.id === DEFAULT_TEMPLATE_ID) return 1
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    })
  }, [templates, includeDrafts])

  const templateOptions = useMemo(() => {
    return filteredTemplates.map((template) => ({
      label: template.name,
      value: template.id,
      subtitle: template.description || undefined,
    }))
  }, [filteredTemplates])

  const pickDefaultTemplateId = useCallback(() => {
    if (!filteredTemplates.length) return undefined

    const def = filteredTemplates.find((t) => t.id === DEFAULT_TEMPLATE_ID)?.id
    if (def) return def

    const firstPublished = filteredTemplates.find((t) => t.published)?.id
    if (firstPublished) return firstPublished

    return filteredTemplates[0]?.id
  }, [filteredTemplates])

  const onFormFinish = async (values: FormValues) => {
    try {
      setIsLoading(true)

      if (!customer) throw new Error('Missing customer context.')
      if (!values.practice) throw new Error('Practice is required.')

      const selectedNextgenPractice =
        nextgenLookup.byValue.get(values.practice) ?? null

      const rejectionReason = getPracticeRejectionReason(
        selectedNextgenPractice,
        existingPractices
      )
      if (rejectionReason || !selectedNextgenPractice) {
        throw new Error(rejectionReason ?? UNSOURCED_PRACTICE_MESSAGE)
      }

      const practice = getPracticeTenantName(selectedNextgenPractice)
      const includeObjectData = !!values.includeObjectData

      const resp = await createTenantFromTemplate({
        customerId: customer.id,
        templateId: values.practiceTemplate || DEFAULT_TEMPLATE_ID,
        tenantName: practice,
        tenantSubdomain: '',
        firstUser: undefined,
        enableTendoEmployeeAccess: false,
        isProduction: false,
        includeObjectData,
        overrideObjectDataIds: [],
        provisionDemoUsers: false,
        provisionDemoUserAuth0Accounts: false,
        demoUserBaseEmail: '',
        practiceId: selectedNextgenPractice.practiceId,
      })

      if (!resp?.tenant?.id) {
        throw new Error('Tenant creation returned no id.')
      }
      queryClient.invalidateQueries(['getCustomer', customer?.id])

      displayGlobalMessage?.({
        type: 'success',
        heading: 'Practice was successfully created.',
      })

      modal.hide()
    } catch (err) {
      basicErrorHandler(err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (templatesLoading) return

    const current = form.getFieldValue('practiceTemplate') as string | undefined
    const currentIsValid = current
      ? filteredTemplates.some((template) => template.id === current)
      : false

    if (!current || !currentIsValid) {
      if (!userSelectedTemplateRef.current) {
        const next = pickDefaultTemplateId()
        if (next) {
          form.setFieldsValue({ practiceTemplate: next })
        }
      } else {
        const next = pickDefaultTemplateId()
        if (next) {
          form.setFieldsValue({ practiceTemplate: next })
        }
      }
    }
  }, [templatesLoading, filteredTemplates, pickDefaultTemplateId, form])

  const practiceValue = useWatch('practice', form)
  const rawPracticeValue = practiceValue ?? ''
  const selectedNextgenPractice =
    nextgenLookup.byValue.get(String(rawPracticeValue)) ?? null
  const normalizedPracticeValue = selectedNextgenPractice
    ? getPracticeTenantName(selectedNextgenPractice)
    : ''
  const isValidPractice =
    !!normalizedPracticeValue && PRACTICE_REGEX.test(normalizedPracticeValue)
  const isDuplicatePractice = normalizedPracticeValue
    ? existingPractices.has(normalizedPracticeValue)
    : false
  const isIncompleteNextgenPractice =
    !!selectedNextgenPractice &&
    !hasRequiredPracticeIdentifiers(selectedNextgenPractice)

  const handleOnOpenChange = (val: boolean) => {
    form.resetFields()
    setSearchValue('')
    modal.toggle(val)
  }

  return (
    <Modal open={modal.visible} onOpenChange={handleOnOpenChange}>
      <Modal.Display aria-label="Add New Practice from Template">
        <Modal.Header
          customTitle={
            <Typography variant="18-medium">
              Add New Practice from Template
            </Typography>
          }
          extras={[
            <Typography key="for" variant="16-regular">
              for
            </Typography>,
            <Typography key="customer-name" variant="16-medium">
              {customer?.name}
            </Typography>,
            <Tag key="type" color={getTagColor(customer)}>
              {getTagLabel(customer)}
            </Tag>,
          ]}
        />
        <Modal.Content>
          <Form name={FORM_NAME} form={form} onFinish={onFormFinish}>
            <FormItem
              required
              label="Select Practice"
              name="practice"
              rules={[
                { required: true, message: 'Practice is required.' },
                {
                  validator: async (_, raw) => {
                    if (!raw || String(raw).trim() === '')
                      return Promise.resolve()

                    const rejectionReason = getPracticeRejectionReason(
                      nextgenLookup.byValue.get(String(raw)),
                      existingPractices
                    )

                    return rejectionReason
                      ? Promise.reject(new Error(rejectionReason))
                      : Promise.resolve()
                  },
                },
              ]}
              description={
                isIncompleteNextgenPractice ? (
                  <Typography variant="12-regular">
                    {MISSING_PRACTICE_IDENTIFIERS_MESSAGE}
                  </Typography>
                ) : rawPracticeValue &&
                  isValidPractice &&
                  !isDuplicatePractice ? (
                  <Flex gap={4} align="center">
                    <CheckCircle
                      sx={{ fontSize: 16, color: colors.nggreen500 }}
                    />
                    <Typography variant="12-regular">
                      {`"${normalizedPracticeValue}" selected successfully.`}
                    </Typography>
                  </Flex>
                ) : undefined
              }
            >
              <Select
                typeahead
                optionLabelProp="labelValue"
                options={visibleOptions}
                loading={nextgenLoading || nextgenFetching}
                value={form.getFieldValue('practice')}
                onSearch={(value) => setSearchValue(value)}
                onSelect={(value) => {
                  const valueStr = String(value)
                  const nextgen = nextgenLookup.byValue.get(valueStr) ?? null

                  form.setFieldsValue({ practice: nextgen ? valueStr : '' })
                }}
                onChange={(value) => {
                  const incoming = String(value)
                  const nextgen = nextgenLookup.byValue.get(incoming) ?? null

                  form.setFieldsValue({ practice: nextgen ? incoming : '' })
                }}
                placeholder="Select"
                notFoundContent={
                  visibleOptions.length > 0 ? null : !hasGcn ? (
                    <Typography>{MISSING_GCN_MESSAGE}</Typography>
                  ) : isInitialPracticesLoading ? (
                    <Flex align="center">
                      <Typography>Loading practices…</Typography>
                    </Flex>
                  ) : searchValue ? (
                    (() => {
                      const isQuerying = nextgenLoading || nextgenFetching

                      if (isQuerying) {
                        return (
                          <Flex align="center">
                            <Typography>Searching for matches…</Typography>
                          </Flex>
                        )
                      }

                      if (!normalizedSearch) {
                        return <Typography>Type to search practices</Typography>
                      }

                      const candidate = normalizeName(String(searchValue || ''))
                      if (!candidate) {
                        return <Typography>No matches found.</Typography>
                      }

                      if (existingPractices.has(candidate)) {
                        const practice = practiceLookup.get(candidate)
                        if (practice) {
                          return (
                            <Flex>
                              <Typography>{`A practice named "${candidate}"`}</Typography>
                              <LinkButton
                                label="already exists"
                                onClick={() =>
                                  navigate(
                                    `/customer-management/${customer?.id}/practices/${practice.id}`
                                  )
                                }
                              />
                              <Typography>for this customer.</Typography>
                            </Flex>
                          )
                        }

                        return (
                          <Typography>
                            {`A practice named "${candidate}" already exists for this customer.`}
                          </Typography>
                        )
                      }

                      return (
                        <Typography>{UNSOURCED_PRACTICE_MESSAGE}</Typography>
                      )
                    })()
                  ) : null
                }
                filterOption={false}
                autoClearSearchValue={false}
              />
            </FormItem>
            <FormItem
              label={
                <Flex align="center" justify="space-between" gap={16} fullWidth>
                  <Typography variant="12-medium">Practice Template</Typography>
                  <Flex align="center" gap={4}>
                    <Switch
                      checked={includeDrafts}
                      onChange={setIncludeDrafts}
                    />
                    <Typography variant="12-regular">
                      Include Draft Templates
                    </Typography>
                  </Flex>
                </Flex>
              }
              name="practiceTemplate"
            >
              <Select
                typeahead
                options={templateOptions}
                loading={templatesLoading}
                placeholder="Select a practice template"
              />
            </FormItem>
            <FormItem name="includeObjectData" valuePropName="checked">
              <Checkbox disabled={!hasObjectData}>
                Include Data Objects
              </Checkbox>
            </FormItem>
          </Form>
          <Flex direction="column" gap={16}>
            <Flex gap={8}>
              <Switch checked={inspectJSON} onChange={setInspectJSON} />
              <Typography>Inspect JSON</Typography>
            </Flex>
            {inspectJSON && selectedTemplateId && (
              <TemplateJSON
                templateId={selectedTemplateId}
                title="Template JSON"
                maxHeight="40vh"
              />
            )}
            {selectedTemplate?.includesObjectData && (
              <Flex gap={8}>
                <Switch
                  checked={inspectDataObjects}
                  onChange={setInspectDataObjects}
                />
                <Typography>Inspect Data Objects</Typography>
              </Flex>
            )}
            {inspectDataObjects && selectedTemplateId && (
              <ObjectDataTable template={selectedTemplate} />
            )}
          </Flex>
        </Modal.Content>
        <Modal.Footer
          footerAction={{
            primaryButton: {
              label: 'Save',
              form: FORM_NAME,
              type: 'submit',
              loading: isLoading,
              disabled: isLoading,
            },
            secondaryButton: {
              label: 'Cancel',
              closeModal: true,
            },
          }}
        />
      </Modal.Display>
    </Modal>
  )
}
